import {useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';

import ArchiveIcon from '@mui/icons-material/Archive';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import {Menu, useTheme} from '@mui/material';

import {ConfirmDeleteDialog} from '../../../../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {EmpionActionMenuItems} from '../../../../../../../components/EmpionActionMenuItems/EmpionActionMenuItems';
import {EmpionButton} from '../../../../../../../components/EmpionButton/EmpionButton';
import {SnackBarContext} from '../../../../../../../contexts/SnackBarContext/SnackBarContext';
import {LocalQueryParams, useQueryParameterState} from '../../../../../../../hooks/useQueryParameterState';
import {
  getEmpionButtonStyles as getEmpionButtonStylesFromTheme,
  getMenuStyles,
} from '../../../../../../../layouts/theme/ThemeHelpers';
import {JobPostingService} from '../../../services/JobPostingService';
import {CompanyLocation, JobPosting} from '../../../types/JobPostingsTypes';
import {JobPostingForm} from '../../forms/JobPostingForm/JobPostingForm';
import {NewJobPostingDialog} from '../../new/NewJobPostingDialog/NewJobPostingDialog';

export interface AssignAdminsProps extends LocalQueryParams {
  assignAdmins?: string;
}
interface MenuButtonActionsProps {
  companyId: string;
  jobPosting: JobPosting;
  handleUpdateJobPosting(jobPosting: JobPosting): void;
  handleDeleteJobPosting(jobPosting: JobPosting): void;
  handleJobPostingStatusChange(jobPosting: JobPosting | undefined, action: 'archive' | 'activate'): void;
  companyLocations: CompanyLocation[];
}

export default function MenuButtonActions({
  companyId,
  jobPosting,
  handleUpdateJobPosting,
  handleDeleteJobPosting,
  handleJobPostingStatusChange,
  companyLocations,
}: MenuButtonActionsProps) {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteJobPostingDialogOpen, setDeleteJobPostingDialogOpen] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {updateQueryParameters} = useQueryParameterState<AssignAdminsProps>();

  const open = Boolean(anchorEl);
  const jobPostingService = new JobPostingService();

  const {dispatchMessage} = useContext(SnackBarContext);
  const {t} = useTranslation();

  const empionButtonStyles = getEmpionButtonStylesFromTheme(open, theme);
  const menuStyles = getMenuStyles(theme);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleEditFormModal = () => {
    setIsModalOpen(!isModalOpen);
    setAnchorEl(null);
  };

  const handleEditFormCancel = () => {
    toggleEditFormModal();
  };
  const publishJobPosting = async () => {
    await jobPostingService.publish(companyId, jobPosting.resourceId as string).then((res) => {
      if (res.ok) {
        if (res.data) {
          handleUpdateJobPosting(res.data);
          dispatchMessage(t('shared.published'), 'success');
        }
      } else {
        dispatchMessage(t('shared.formSubmit.publishError', res.error?.extra_details || 'Undefined error'), 'error');
      }
    });
  };

  const archiveJobPosting = async () => {
    await jobPostingService.archive(companyId, jobPosting.resourceId as string).then((res) => {
      if (res.ok) {
        if (res.data) {
          handleJobPostingStatusChange(res.data, 'archive');
          dispatchMessage(t('shared.formSubmit.success'), 'success');
        }
      } else {
        dispatchMessage(t('shared.formSubmit.error', res.error?.extra_details || 'Undefined error'), 'error');
      }
    });
  };

  const handleJobPostingDeleteDialog = async () => {
    setDeleteJobPostingDialogOpen(!deleteJobPostingDialogOpen);
  };

  const deleteJobPosting = async () => {
    await jobPostingService.delete(companyId, jobPosting.resourceId as string).then((res) => {
      if (res.ok) {
        dispatchMessage(t('shared.success'), 'success');
        handleDeleteJobPosting(jobPosting);
      } else {
        dispatchMessage(t('shared.formSubmit.error'), 'error');
      }
    });
  };

  const menuItems = [
    {
      key: 'edit',
      label: t('shared.formButtons.edit'),
      onClick: () => {
        toggleEditFormModal();
      },
      icon: <EditIcon />,
      disabled: false,
      variant: 'body1',
    },
    {
      key: 'publish',
      label: t('shared.formButtons.publish'),
      onClick: async () => {
        await publishJobPosting();
      },
      icon: <PublishIcon />,
      disabled: !!jobPosting.publishedAt,
      variant: 'body1',
    },
    {
      key: 'archive',
      label: t('shared.formButtons.archive'),
      onClick: () => {
        archiveJobPosting();
      },
      icon: <ArchiveIcon />,
      disabled: !jobPosting.publishedAt,
      variant: 'body1',
    },
    // NOTE: Disabled for now but keeping the code for future use

    // {
    //   key: 'delete',
    //   label: t('shared.formButtons.delete'),
    //   onClick: () => {
    //     handleJobPostingDeleteDialog();
    //   },
    //   icon: <DeleteIcon />,
    //   disabled: jobPosting.publishedAt ? true : false,
    //   variant: 'body1',
    // },
    {
      key: 'assignAdmins',
      label: t('shared.formButtons.assign'),
      onClick: () => {
        toggleEditFormModal();
        updateQueryParameters({assignAdmins: 'true'});
      },
      icon: <AssignmentIndIcon />,
      disabled: false,
      variant: 'body1',
    },
  ];

  return (
    <>
      <EmpionButton
        id={`${jobPosting.resourceId}-button`}
        endIcon={<ArrowDropDownIcon />}
        onClick={handleClick}
        sx={{
          ...empionButtonStyles,
        }}
      >
        {t('companyJobPostings.formButtons.actions')}
      </EmpionButton>

      <Menu
        id='basic-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        sx={menuStyles}
      >
        <EmpionActionMenuItems
          menuItems={menuItems}
          variant='body1'
          sx={{
            mr: '16px',
            ml: '26px',
            borderRadius: '10px',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'primary.dark' : 'background.menu',
              width: '100%',
            },
          }}
        />
      </Menu>
      <ConfirmDeleteDialog
        open={deleteJobPostingDialogOpen}
        dialogContent={t('companyJobPostings.deleteDialog.dialogContent')}
        handleConfirm={deleteJobPosting}
        handleCancel={handleJobPostingDeleteDialog}
      />
      <NewJobPostingDialog open={isModalOpen} onClose={toggleEditFormModal}>
        <JobPostingForm
          companyId={companyId}
          onCancel={handleEditFormCancel}
          afterSubmit={handleUpdateJobPosting}
          companyLocations={companyLocations}
          jobPosting={jobPosting}
        />
      </NewJobPostingDialog>
    </>
  );
}
